import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  GitBranch,
  Pencil,
  Plus,
  Trash2,
  Star,
  Check,
  GripVertical,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api, extractApiError } from '../../lib/api'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { TextField, TextAreaField } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import {
  departmentList,
  tenantWorkspacesResponse,
  workflowsResponse,
} from '../../lib/types'
import { CONTAINS_LETTER, CONTAINS_EMOJI } from '../../lib/validators'

const workflowFormSchema = z.object({
  name: z
    .string({ message: 'Workflow Name is required.' })
    .trim()
    .min(1, 'Workflow Name is required.')
    .min(2, 'Workflow Name must be at least 2 characters.')
    .max(120, 'Workflow Name must be at most 120 characters.')
    // Require a real letter and block emojis — mirrors the shared entity-name
    // rule + the backend workflow DTO.
    .regex(CONTAINS_LETTER, 'Workflow Name must contain at least one letter.')
    .refine((v) => !CONTAINS_EMOJI.test(v), 'Workflow Name cannot contain emojis.'),
  description: z
    .string()
    .trim()
    .max(500, 'Description must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
})

/** Same swatch palette the Space status editor uses, so a Workflow
 *  state colour reads identically to a Space task-status colour in the
 *  rest of the product. */
const STATE_COLOR_PALETTE = [
  '#94a3b8',
  '#0EA5E9',
  '#3B82F6',
  '#A855F7',
  '#EC4899',
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#10B981',
  '#14B8A6',
  '#22D3EE',
]

/** Each state is Active or Inactive. Active states appear in Spaces
 *  (status picker, board columns, list sections); Inactive states are
 *  hidden. The LAST active state (by order) is the workflow's completion
 *  state — the backend derives the stored `category` from this, so the
 *  editor no longer picks a category directly. */
const ACTIVE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

/** Stored `active` defaults to true for legacy states saved before the
 *  flag existed. */
function isActiveState(s) {
  return s.active !== false
}

/** Key of the last active state (by array order) — the completion state.
 *  Null when nothing is active. */
function completionStateKey(states) {
  let key = null
  for (const s of states) if (isActiveState(s)) key = s.key
  return key
}

const DEFAULT_NEW_WORKFLOW = {
  name: '',
  description: '',
  isDefault: false,
  states: [
    { key: 'todo', label: 'TO DO', color: '#94a3b8', active: true, order: 0 },
    {
      key: 'in_progress',
      label: 'IN PROGRESS',
      color: '#3b82f6',
      active: true,
      order: 1,
    },
    { key: 'complete', label: 'COMPLETE', color: '#10b981', active: true, order: 2 },
  ],
  transitions: [],
}

export function WorkflowsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  // Pending delete target — holds the workflow row so the confirm
  // modal can name it. Null = no delete in flight.
  const [deleteTarget, setDeleteTarget] = useState(null)

  const query = useQuery({
    queryKey: ['workflows'],
    queryFn: async () => {
      const res = await api.get('/workflows')
      return workflowsResponse.parse(res.data)
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (id) => api.delete(`/workflows/${id}`),
    onSuccess: () => {
      toast.success('Workflow deleted successfully.')
      void qc.invalidateQueries({ queryKey: ['workflows'] })
      setDeleteTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const rows = query.data?.data ?? []

  return (
    <div>
      <Breadcrumb current="Workflows" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <GitBranch
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Workflows
          </h1>
          <p className="text-[color:var(--color-ink-muted)] mt-1 text-sm">
            Set the steps tasks follow.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...DEFAULT_NEW_WORKFLOW, _mode: 'create' })}
          className="inline-flex items-center gap-2 whitespace-nowrap shrink-0 rounded-[10px] bg-[color:var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-accent-strong)] transition-colors"
        >
          <Plus size={16} /> New Workflow
        </button>
      </div>

      {query.isLoading ? (
        <div className="text-[color:var(--color-ink-muted)]">Loading…</div>
      ) : query.isError ? (
        <div className="text-red-600 text-sm">
          {extractApiError(query.error).message}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-[color:var(--color-ink-muted)] text-sm">
          No workflows yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {rows.map((w) => (
            <WorkflowCard
              key={w.id}
              workflow={w}
              onEdit={() => setEditing({ ...w, _mode: 'edit' })}
              onDelete={() => {
                if (w.isDefault) {
                  // Hard pre-check guard — the default workflow can
                  // never be deleted, so we toast and don't open the
                  // confirm modal.
                  toast.error('Cannot delete the default workflow.')
                  return
                }
                setDeleteTarget(w)
              }}
            />
          ))}
        </div>
      )}

      <DepartmentWorkflowAssignments workflows={rows} />

      {editing && (
        <WorkflowEditorDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['workflows'] })
            setEditing(null)
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={
          deleteTarget
            ? `Delete workflow "${deleteTarget.name}"?`
            : 'Delete this workflow?'
        }
        message="Tasks already running on this workflow will stay on it, but no new lists can pick it. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        confirming={removeMutation.isPending}
        onCancel={() =>
          removeMutation.isPending ? undefined : setDeleteTarget(null)
        }
        onConfirm={() => {
          if (deleteTarget?.id) removeMutation.mutate(deleteTarget.id)
        }}
      />
    </div>
  )
}

function WorkflowCard({ workflow, onEdit, onDelete }) {
  return (
    <div className="rounded-[12px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] truncate">
              {workflow.name}
            </h2>
            {workflow.isDefault && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-xs px-2 py-0.5 dark:bg-white/[0.06] dark:text-amber-300">
                <Star size={12} /> Default
              </span>
            )}
          </div>
          {workflow.description && (
            <p className="text-[color:var(--color-ink-muted)] text-sm mt-1">
              {workflow.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1 rounded-[8px] border border-[color:var(--color-rule)] px-2.5 py-1.5 text-xs text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
          >
            <Pencil size={14} /> Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={workflow.isDefault}
            className="inline-flex items-center gap-1 rounded-[8px] border border-[color:var(--color-rule)] px-2.5 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[...workflow.states]
          .sort((a, b) => a.order - b.order)
          .map((s) => (
            <span
              key={s.key}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium"
              style={{
                backgroundColor: `${s.color}1a`,
                color: s.color || '#111827',
                border: `1px solid ${s.color}40`,
              }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: s.color || '#94a3b8' }}
              />
              {s.label}
            </span>
          ))}
      </div>
      {workflow.transitions.length > 0 && (
        <div className="text-[12px] text-[color:var(--color-ink-muted)] mt-2">
          {workflow.transitions.length} transition
          {workflow.transitions.length === 1 ? '' : 's'} configured
        </div>
      )}
    </div>
  )
}

function WorkflowEditorDialog({ initial, onClose, onSaved }) {
  const isEdit = initial._mode === 'edit'
  const [isDefault, setIsDefault] = useState(Boolean(initial.isDefault))
  const [states, setStates] = useState(
    [...(initial.states ?? [])]
      .sort((a, b) => a.order - b.order)
      // Legacy states (saved before the Active flag) default to active.
      .map((s) => ({ ...s, active: isActiveState(s) })),
  )
  const [transitions, setTransitions] = useState(initial.transitions ?? [])
  const [statesError, setStatesError] = useState(null)
  const [, setTransitionsError] = useState(null)
  // MouseSensor keeps desktop behaviour identical; TouchSensor is delay-based
  // so a swipe scrolls the list while a press-and-hold starts a drag-reorder.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const form = useForm({
    resolver: zodResolver(workflowFormSchema),
    defaultValues: {
      name: initial.name ?? '',
      description: initial.description ?? '',
    },
  })


  const saveMutation = useMutation({
    mutationFn: async (values) => {
      const body = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        isDefault,
        // Send `active`; the backend derives the stored category (last
        // active state = completion).
        states: states.map((s, i) => ({
          key: s.key,
          label: s.label,
          color: s.color || '#94a3b8',
          active: isActiveState(s),
          order: i,
        })),
        transitions,
      }
      if (isEdit) {
        const res = await api.patch(`/workflows/${initial.id}`, body)
        return res.data
      }
      const res = await api.post('/workflows', body)
      return res.data
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Workflow updated successfully.' : 'Workflow created successfully.')
      onSaved()
    },
    onError: (err) => {
      const e = extractApiError(err)
      if (e.fields?.name) form.setError('name', { message: e.fields.name })
      if (e.fields?.description)
        form.setError('description', { message: e.fields.description })
      setStatesError(e.fields?.states ?? null)
      setTransitionsError(e.fields?.transitions ?? null)
      if (!e.fields || Object.keys(e.fields).length === 0) toast.error(e.message)
    },
  })

  function addState() {
    // Keys are no longer surfaced in the UI but stay required server-side
    // (and stable for the transitions graph), so derive a unique
    // `state_N` that isn't already in use.
    setStates((cur) => {
      const used = new Set(cur.map((s) => s.key))
      let n = cur.length + 1
      while (used.has(`state_${n}`)) n += 1
      return [
        ...cur,
        {
          key: `state_${n}`,
          label: `State ${cur.length + 1}`,
          color: '#94a3b8',
          active: true,
          order: cur.length,
        },
      ]
    })
  }

  function updateState(idx, patch) {
    setStates((cur) => cur.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  }

  function removeState(idx) {
    const target = states[idx]
    // Block the delete-click itself when tasks still sit on this status,
    // rather than optimistically dropping the row and only failing on Save
    // (the row would appear to vanish first). `taskCount` comes from the
    // list endpoint; new/unsaved states have none. The backend guard stays
    // the authoritative check for anything that changed since load.
    if ((target.taskCount ?? 0) > 0) {
      setStatesError(
        `Move tasks off "${target.label}" (${target.taskCount}) before removing.`,
      )
      return
    }
    setStatesError(null)
    setStates((cur) => cur.filter((_, i) => i !== idx))
    setTransitions((cur) =>
      cur.filter((t) => t.from !== target.key && t.to !== target.key),
    )
  }

  function onStateDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setStates((cur) => {
      const from = cur.findIndex((s) => s.key === active.id)
      const to = cur.findIndex((s) => s.key === over.id)
      if (from === -1 || to === -1) return cur
      return arrayMove(cur, from, to)
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Workflow' : 'New Workflow'}
      description="Define the statuses a task can be in and the moves allowed between them."
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="workflow-editor-form"
            loading={saveMutation.isPending}
          >
            <Check size={16} />
            {isEdit ? 'Save Changes' : 'Create Workflow'}
          </Button>
        </>
      }
    >
      <form
        id="workflow-editor-form"
        onSubmit={form.handleSubmit((v) => {
          // At least one Active state — mirrors the backend guard so the
          // user sees the inline error without a round-trip.
          if (!states.some(isActiveState)) {
            setStatesError('At least one state must be Active.')
            return
          }
          saveMutation.mutate(v)
        })}
        noValidate
        className="space-y-4"
      >
        <TextField
          label="Workflow Name"
          required
          placeholder="Enter a Workflow Name"
          minLength={2}
          maxLength={120}
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />

        <TextAreaField
          label="Description"
          placeholder="Optional"
          rows={2}
          maxLength={500}
          error={form.formState.errors.description?.message}
          {...form.register('description')}
        />

        <ToggleRow
          label="Use as default workflow for this company"
          description="Applied to any list or project that hasn't picked a custom workflow."
          value={isDefault}
          onChange={setIsDefault}
        />

        <div>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
              States{' '}
              <span
                className="text-[color:var(--color-priority-urgent)]"
                aria-hidden="true"
              >
                *
              </span>
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addState}
            >
              <Plus size={14} /> Add State
            </Button>
          </div>
          {/* Make it clear the states are a required, customisable part of the
           *  workflow — not optional. They're pre-filled with the default
           *  statuses as a starting point, so without this hint users tended to
           *  keep them and end up with a near-copy of the Default workflow. */}
          <p className="mb-2 text-xs text-[color:var(--color-ink-muted)]">
            These start from the default statuses. Rename, recolour, add, or
            remove them to suit this workflow — at least 2 are required.
            Inactive states are hidden from Spaces; the last Active state is
            the Completed state.
          </p>
          {statesError ? (
            <p
              role="alert"
              className="mb-2 text-xs text-[color:var(--color-priority-urgent)]"
            >
              {statesError}
            </p>
          ) : null}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onStateDragEnd}
          >
            <SortableContext
              items={states.map((s) => s.key)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {states.map((s, i) => (
                  <StateRow
                    key={s.key}
                    state={s}
                    isCompletion={s.key === completionStateKey(states)}
                    onUpdate={(patch) => updateState(i, patch)}
                    onRemove={() => removeState(i)}
                    disableRemove={states.length <= 2}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        {/* Transitions editor was removed from the dialog. The
         *  `transitions` state, addTransition / updateTransition /
         *  removeTransition handlers, and the save body still wire
         *  through transitions (defaulting to the existing list on
         *  edit, [] on create) so re-enabling the UI later is a
         *  one-block revert. An empty transitions array means
         *  "any state → any state". */}
      </form>
    </Modal>
  )
}

/** A single sortable state row in the Workflow editor. The grip handle
 *  carries the dnd-kit drag listeners so the label input, colour swatch
 *  and category select stay fully interactive; the whole row lifts (and
 *  animates into place) on drag via the sortable transform. */
function StateRow({ state: s, isCompletion, onUpdate, onRemove, disableRemove }) {
  const active = isActiveState(s)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: s.key })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        'flex flex-wrap items-center gap-2 p-2 rounded-lg border bg-[color:var(--color-card)] ' +
        (isDragging
          ? 'border-[color:var(--color-accent)] shadow-lg'
          : 'border-[color:var(--color-rule)]')
      }
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder state"
        title="Drag to reorder"
        className="shrink-0 p-1 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical size={16} />
      </button>
      <input
        type="text"
        value={s.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        className="w-40 h-8 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
        placeholder="Label"
      />
      <StateColorPicker
        color={s.color || '#94a3b8'}
        onChange={(color) => onUpdate({ color })}
      />
      <Select
        sizeVariant="sm"
        fullWidth={false}
        wrapperClassName="min-w-[7rem]"
        value={active ? 'active' : 'inactive'}
        onChange={(e) => onUpdate({ active: e.target.value === 'active' })}
      >
        {ACTIVE_OPTIONS.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </Select>
      {/* The last Active state is the workflow's completion state. */}
      {active && isCompletion ? (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[color:var(--color-success)] text-[color:var(--color-on-success)]">
          <Check size={11} />
          Completed
        </span>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        disabled={disableRemove}
        aria-label="Delete state"
        title="Delete state"
        className="ml-auto p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Trash2 size={14} />
      </button>
    </div>
  )
}

/** Toggle pill + descriptive row — mirrors the `ToggleRow` used in
 *  SpaceCreateDialog so this dialog's "default workflow" switch feels
 *  identical to "Make private" elsewhere. */
function ToggleRow({ label, description, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
      <div className="min-w-0">
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
          {label}
        </p>
        {description ? (
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
            {description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ' +
          (value
            ? 'bg-[color:var(--color-accent-strong)]'
            : 'bg-slate-300')
        }
      >
        <span
          aria-hidden="true"
          className={
            'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ' +
            (value ? 'translate-x-[1.375rem]' : 'translate-x-0.5')
          }
        />
      </button>
    </div>
  )
}

/** Tenant-wide table that lets the Company Admin pick which Workflow
 *  each Department runs on. A Space inherits its task workflow from its
 *  Department; selecting "Default" stores `null` on the department and
 *  the task status picker falls through to the tenant default workflow. */
function DepartmentWorkflowAssignments({ workflows }) {
  const qc = useQueryClient()
  // Departments are workspace-scoped, so first enumerate the tenant's
  // workspaces, then fetch each one's departments in parallel.
  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'admin', 'all'],
    queryFn: async () => {
      const res = await api.get('/workspaces/admin/all')
      return tenantWorkspacesResponse.parse(res.data)
    },
  })
  const workspaces = workspacesQuery.data?.data ?? []
  const hasMultipleWorkspaces = workspaces.length > 1
  const workspacesKey = workspaces
    .map((w) => w.id)
    .sort()
    .join(',')

  const deptsQuery = useQuery({
    queryKey: ['departments', '__workflow_assign__', workspacesKey],
    queryFn: async () => {
      const results = await Promise.all(
        workspaces.map(async (w) => {
          try {
            const res = await api.get(`/workspaces/${w.id}/departments`)
            const parsed = departmentList.parse(res.data)
            return (parsed.data ?? []).map((d) => ({
              ...d,
              workspaceId: d.workspaceId ?? w.id,
              workspaceName: w.name,
            }))
          } catch {
            // Drop a failing workspace silently — the rest still render.
            return []
          }
        }),
      )
      return results
        .flat()
        .sort((a, b) =>
          a.workspaceName === b.workspaceName
            ? a.name.localeCompare(b.name)
            : a.workspaceName.localeCompare(b.workspaceName),
        )
    },
    enabled: workspaces.length > 0,
  })

  const assignMutation = useMutation({
    mutationFn: async ({ workspaceId, departmentId, workflowId }) => {
      await api.patch(
        `/workspaces/${workspaceId}/departments/${departmentId}`,
        { workflowId },
      )
    },
    onSuccess: () => {
      toast.success('Department workflow updated successfully.')
      // Refresh both — workflow cards show the new "Used by …" chips,
      // department list shows the new dropdown selection.
      void qc.invalidateQueries({ queryKey: ['workflows'] })
      void qc.invalidateQueries({ queryKey: ['departments'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const defaultWorkflow = workflows.find((w) => w.isDefault)
  const rows = deptsQuery.data ?? []
  const isLoading =
    workspacesQuery.isLoading ||
    (workspaces.length > 0 && deptsQuery.isLoading)
  const isError = workspacesQuery.isError || deptsQuery.isError
  const queryError = workspacesQuery.error || deptsQuery.error

  return (
    <section className="mt-8">
      <div className="mb-3">
        <h2 className="font-display text-xl text-[color:var(--color-ink-strong)]">
          Departments &amp; Their Workflows
        </h2>
        <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
          Pick which workflow each department runs on. Departments left on
          <span className="font-medium"> Default</span> follow whichever
          workflow is currently marked as the tenant default.
        </p>
      </div>
      {isLoading ? (
        <div className="text-[color:var(--color-ink-muted)] text-sm">
          Loading departments…
        </div>
      ) : isError ? (
        <div className="text-red-600 text-sm">
          {extractApiError(queryError).message}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-[color:var(--color-ink-muted)] text-sm">
          No departments yet. Add a department first, then assign its
          workflow here.
        </div>
      ) : (
        <div className="rounded-[12px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          {rows.map((dept, idx) => {
            // A workflowId that no longer matches any workflow (its workflow
            // was deleted) is a DANGLING reference. The backend already treats
            // it as the tenant default (getTenantTaskStatuses), so surface it
            // as Default here too — showing "Default" and selecting it in the
            // picker — instead of the confusing "Unknown workflow".
            const matchedWorkflow = dept.workflowId
              ? workflows.find((w) => w.id === dept.workflowId)
              : null
            const currentValue = matchedWorkflow ? dept.workflowId : '__default__'
            const resolvedName = matchedWorkflow
              ? matchedWorkflow.name
              : defaultWorkflow
                ? `Default (${defaultWorkflow.name})`
                : 'Default'
            return (
              <div
                key={dept.id}
                className={
                  'flex items-center justify-between gap-3 px-4 py-3 ' +
                  (idx > 0
                    ? 'border-t border-[color:var(--color-rule)]'
                    : '')
                }
              >
                <div className="min-w-0 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="h-7 w-7 rounded-md shrink-0 flex items-center justify-center text-[12px] font-semibold text-white"
                    style={{ backgroundColor: '#64748b' }}
                  >
                    {dept.name.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
                      {dept.name}
                    </p>
                    <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
                      {hasMultipleWorkspaces
                        ? `${dept.workspaceName} · `
                        : ''}
                      runs on{' '}
                      <span className="text-[color:var(--color-ink)]">
                        {resolvedName}
                      </span>
                    </p>
                  </div>
                </div>
                <Select
                  sizeVariant="sm"
                  fullWidth={false}
                  wrapperClassName="min-w-[14rem]"
                  value={currentValue}
                  disabled={assignMutation.isPending}
                  onChange={(e) => {
                    const nextWorkflowId =
                      e.target.value === '__default__' ? null : e.target.value
                    if ((dept.workflowId ?? null) === nextWorkflowId) return
                    assignMutation.mutate({
                      workspaceId: dept.workspaceId,
                      departmentId: dept.id,
                      workflowId: nextWorkflowId,
                    })
                  }}
                >
                  {/* The current default workflow is hidden from the
                   *  explicit list because the synthetic "Default" entry
                   *  already resolves to it. Without this filter the
                   *  dropdown shows two functionally identical options
                   *  (auto-follow vs. pin-to-current-default). */}
                  <option value="__default__">
                    Default
                    {defaultWorkflow ? ` (${defaultWorkflow.name})` : ''}
                  </option>
                  {workflows
                    .filter((w) => !w.isDefault)
                    .map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                </Select>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

/** Swatch popover that mirrors the Space TaskStatusEditor colour picker.
 *  The palette grid is rendered into a `<body>`-level portal with fixed
 *  positioning so it floats over the Modal's scroll container instead
 *  of getting clipped at the bottom — earlier versions had the popover
 *  positioned with `absolute top-full` which sat inside the Modal's
 *  `overflow-y-auto` body and could only flow downward into the
 *  scrollable area, never over the surrounding chrome. */
function StateColorPicker({ color, onChange }) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)

  // Anchor the popover under the trigger and pin its right edge so it
  // doesn't push past the modal/viewport edge. Re-measures on scroll /
  // resize because the modal body is scrollable.
  useLayoutEffect(() => {
    if (!open) return
    function update() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      setCoords({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const t = e.target
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change state colour"
        title="Change colour"
        className="h-7 w-9 rounded-md border border-[color:var(--color-rule)] cursor-pointer hover:opacity-90 shrink-0"
        style={{ backgroundColor: color }}
      />
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="dialog"
              aria-label="State colour"
              style={{
                position: 'fixed',
                top: coords.top,
                right: coords.right,
                zIndex: 100,
              }}
              className="w-[12.5rem] p-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl"
            >
              <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                Colour
              </p>
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
              >
                {STATE_COLOR_PALETTE.map((c) => {
                  const active = c.toLowerCase() === (color || '').toLowerCase()
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        onChange(c)
                        setOpen(false)
                      }}
                      aria-label={`Pick ${c}`}
                      title={c}
                      className={
                        'relative h-5 w-5 rounded-full transition-transform cursor-pointer motion-safe:hover:scale-110 ' +
                        (active
                          ? 'ring-2 ring-offset-1 ring-[color:var(--color-ink-strong)]'
                          : '')
                      }
                      style={{ backgroundColor: c }}
                    >
                      {active ? (
                        <Check
                          size={10}
                          className="absolute inset-0 m-auto text-white drop-shadow"
                          aria-hidden="true"
                        />
                      ) : null}
                    </button>
                  )
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
